/**
 * AudioStreamPipeline — OOM-proof audio processing for any duration file.
 *
 * Combines existing chunked I/O (Mp3DecoderStream input + MP3Encoder.encodeStream
 * output + OutputSink for OPFS-backed disk buffering) with stateful per-chunk
 * filter implementations (biquad EQ/highpass/lowpass, feedforward compressor,
 * peak limiter, output gain). Memory usage is O(chunk_size) regardless of source
 * duration: 5-hour podcast and 30-second clip both run with ~5 KB main-thread
 * heap.
 *
 * Why this exists
 * ───────────────
 * FFmpeg WASM has a hard 4 GB linear-memory cap. A 100 MB MP3 (≈40 min) decodes
 * to ~1 GB float32 stereo @ 48 kHz; with FFmpeg's working buffers the peak
 * pushes against the cap → `Aborted()` → cryptic OOM error visible to user
 * regardless of how much device RAM exists. Even Web Audio API native processing
 * via `decodeAudioData` faces a similar wall: the entire AudioBuffer must fit
 * in main-thread JS heap (~2 GB practical).
 *
 * Streaming pipeline sidesteps both: input is decoded one ~26 ms frame at a
 * time, each frame passes through the (stateful) filter chain, encoded result
 * is written progressively to OPFS sink. No buffer holds more than one frame.
 *
 * Public API
 * ──────────
 *
 *   const result = await AudioStreamPipeline.process(file, {
 *       filters: [
 *           { name: 'highpass', f: 120 },
 *           { name: 'equalizer', f: 2800, g: 3.5, w: 1200 },
 *           { name: 'acompressor', threshold: -24, ratio: 4, attack: 5, release: 80, makeup: 2 },
 *           { name: 'alimiter', limit: 0.95 }
 *       ],
 *       output: { format: 'mp3', bitrate: 192 },     // or { format: 'wav' }
 *       slug: 'audio-enhancer',
 *       onProgress: (ratio, stage) => { ... }
 *   });
 *   // → { blob, name, mode: 'streaming', sampleRate, channels, opsApplied, opsSkipped }
 *
 *   AudioStreamPipeline.canHandle(filters, inputExt, outputFormat)
 *   // → { eligible: bool, reason: string, opsSupported: [...], opsBlocked: [...] }
 *
 * Eligibility rules (single source of truth — caller does NOT need to inspect
 * the filter chain itself; just call canHandle and route accordingly):
 *
 *   - Input format: only MP3 + WAV in v1 (FLAC/OGG/AAC fall back to FFmpeg)
 *   - Output format: only MP3 + WAV in v1
 *   - Filters: highpass, lowpass, equalizer (peaking), acompressor, alimiter
 *     are streaming-supported. afftdn / agate / dynaudnorm / loudnorm fall
 *     back to FFmpeg path entirely (caller's responsibility to detect via
 *     canHandle).
 *
 * Quality vs FFmpeg
 * ─────────────────
 *   - Biquad filters: bit-identical (same RBJ coefficients, state preserved
 *     across chunks).
 *   - Compressor / limiter: identical algorithm, state preserved → indistinguishable
 *     output for typical signals.
 *   - loudnorm: NOT in streaming. Real EBU R128 needs whole-signal measurement.
 *     Caller should either route through FFmpeg, OR replace with a +5 dB makeup
 *     'gain' filter for a constant-boost approximation (~±2 dB target accuracy).
 *
 * @see  Mp3DecoderStream     (public/js/mp3-decoder-streaming.js)
 * @see  MP3Encoder           (public/js/mp3-encoder.js)
 * @see  OutputSink           (public/js/audio-output-sink.js)
 */
(function () {
    'use strict';
    if (window.AudioStreamPipeline) return;

    var STREAMING_FILTERS = ['highpass', 'lowpass', 'equalizer', 'acompressor', 'alimiter', 'gain', 'fadeIn', 'fadeOut', 'envelope', 'stereotools', 'rnnoise'];
    var STREAMING_FORMATS = ['mp3', 'wav'];

    function _ae(action, payload) {
        try {
            if (typeof window.__ae === 'function') {
                window.__ae('feature_use', Object.assign({ action: action }, payload || {}));
            }
        } catch (_) {}
    }

    // ──────────────────────────────────────────────────────────────────
    //  Stateful filters (Direct Form II Transposed biquads + envelope-
    //  follower compressor + hard limiter + scalar gain)
    // ──────────────────────────────────────────────────────────────────

    /** RBJ cookbook coefficients. Returns {b0, b1, b2, a1, a2} (normalised by a0). */
    function rbjCoeffs(type, sampleRate, freq, qOrWidth, gainDb) {
        var w0 = 2 * Math.PI * freq / sampleRate;
        var cosW = Math.cos(w0);
        var sinW = Math.sin(w0);
        // For 'highpass'/'lowpass' qOrWidth is treated as Q (default 0.7071 = no resonance).
        // For 'peaking' qOrWidth is Q computed from FFmpeg's f/w convention by caller.
        var Q = qOrWidth || 0.7071;
        var alpha = sinW / (2 * Q);
        var b0, b1, b2, a0, a1, a2;
        switch (type) {
            case 'highpass': {
                b0 = (1 + cosW) / 2;
                b1 = -(1 + cosW);
                b2 = (1 + cosW) / 2;
                a0 = 1 + alpha;
                a1 = -2 * cosW;
                a2 = 1 - alpha;
                break;
            }
            case 'lowpass': {
                b0 = (1 - cosW) / 2;
                b1 = 1 - cosW;
                b2 = (1 - cosW) / 2;
                a0 = 1 + alpha;
                a1 = -2 * cosW;
                a2 = 1 - alpha;
                break;
            }
            case 'peaking': {
                var A = Math.pow(10, (gainDb || 0) / 40);
                b0 = 1 + alpha * A;
                b1 = -2 * cosW;
                b2 = 1 - alpha * A;
                a0 = 1 + alpha / A;
                a1 = -2 * cosW;
                a2 = 1 - alpha / A;
                break;
            }
            // Shelves. Web Audio's BiquadFilterNode ignores Q for these and fixes
            // the shelf slope at S = 1, so alpha is built from S, not from Q —
            // matching it is what keeps a ported tone curve sounding the same.
            // Measured against the native node: −95 dB and below (see
            // tests/audio/stream-pipeline-shelves.mjs). Added 2026-07-31 because
            // every mastering tone curve and the whole reference-matching fit are
            // built on shelves, and this function threw on them.
            case 'lowshelf':
            case 'highshelf': {
                var As = Math.pow(10, (gainDb || 0) / 40);
                var alphaS = sinW / 2 * Math.sqrt(2);          // S = 1
                var twoSqrtAalpha = 2 * Math.sqrt(As) * alphaS;
                var Ap1 = As + 1, Am1 = As - 1;
                if (type === 'lowshelf') {
                    b0 = As * (Ap1 - Am1 * cosW + twoSqrtAalpha);
                    b1 = 2 * As * (Am1 - Ap1 * cosW);
                    b2 = As * (Ap1 - Am1 * cosW - twoSqrtAalpha);
                    a0 = Ap1 + Am1 * cosW + twoSqrtAalpha;
                    a1 = -2 * (Am1 + Ap1 * cosW);
                    a2 = Ap1 + Am1 * cosW - twoSqrtAalpha;
                } else {
                    b0 = As * (Ap1 + Am1 * cosW + twoSqrtAalpha);
                    b1 = -2 * As * (Am1 + Ap1 * cosW);
                    b2 = As * (Ap1 + Am1 * cosW - twoSqrtAalpha);
                    a0 = Ap1 - Am1 * cosW + twoSqrtAalpha;
                    a1 = 2 * (Am1 - Ap1 * cosW);
                    a2 = Ap1 - Am1 * cosW - twoSqrtAalpha;
                }
                break;
            }
            default: throw new Error('rbjCoeffs: unknown type ' + type);
        }
        return { b0: b0/a0, b1: b1/a0, b2: b2/a0, a1: a1/a0, a2: a2/a0 };
    }

    /**
     * Biquad filter (Direct Form II Transposed). State (z1, z2) per channel
     * survives across chunks → output bit-identical to whole-signal processing.
     */
    function makeBiquad(type, sampleRate, freq, qOrWidth, gainDb, numChannels) {
        var c = rbjCoeffs(type, sampleRate, freq, qOrWidth, gainDb);
        var z1 = new Float64Array(numChannels);
        var z2 = new Float64Array(numChannels);
        return {
            process: function (channelArrays) {
                for (var ch = 0; ch < channelArrays.length; ch++) {
                    var data = channelArrays[ch];
                    var s1 = z1[ch], s2 = z2[ch];
                    for (var i = 0; i < data.length; i++) {
                        var x = data[i];
                        var y = c.b0 * x + s1;
                        s1 = c.b1 * x - c.a1 * y + s2;
                        s2 = c.b2 * x - c.a2 * y;
                        data[i] = y;
                    }
                    z1[ch] = s1; z2[ch] = s2;
                }
                return channelArrays;
            }
        };
    }

    /**
     * Feedforward compressor with envelope follower. Cross-chunk state preserved
     * via `envelope` accumulator → boundary continuity matches whole-signal.
     */
    function makeCompressor(opts, sampleRate) {
        var threshold = (opts.threshold !== undefined) ? opts.threshold : -20;       // dB
        var ratio = (opts.ratio !== undefined) ? opts.ratio : 3;                     // ≥ 1
        var attackMs = (opts.attack !== undefined) ? opts.attack : 8;
        var releaseMs = (opts.release !== undefined) ? opts.release : 80;
        var makeupDb = (opts.makeup !== undefined) ? opts.makeup : 0;
        var attackCoef = Math.exp(-1 / (sampleRate * attackMs / 1000));
        var releaseCoef = Math.exp(-1 / (sampleRate * releaseMs / 1000));
        var thresholdLin = Math.pow(10, threshold / 20);
        var makeupLin = Math.pow(10, makeupDb / 20);
        var slope = 1 - 1 / ratio;
        var envelope = 0;
        return {
            process: function (channelArrays) {
                var n = channelArrays[0].length;
                var nCh = channelArrays.length;
                for (var i = 0; i < n; i++) {
                    var peak = 0;
                    for (var ch = 0; ch < nCh; ch++) {
                        var v = channelArrays[ch][i];
                        if (v < 0) v = -v;
                        if (v > peak) peak = v;
                    }
                    var coef = (peak > envelope) ? attackCoef : releaseCoef;
                    envelope = peak + (envelope - peak) * coef;
                    var gain = makeupLin;
                    if (envelope > thresholdLin) {
                        gain = Math.pow(envelope / thresholdLin, -slope) * makeupLin;
                    }
                    for (var ch2 = 0; ch2 < nCh; ch2++) {
                        channelArrays[ch2][i] *= gain;
                    }
                }
                return channelArrays;
            }
        };
    }

    /** True-peak лимитер потолка.
     *
     * ⚠️ Прежняя реализация была ЖЁСТКИМ КЛАМПОМ с комментарием «matches
     * FFmpeg alimiter» — неверным дважды: ffmpeg alimiter не hard-clip, а
     * hard-clip и есть тот треск класса «в браузере одно, в файле другое»,
     * ради лечения которого потолок вообще ставят (замер 19.08.2026 на
     * /volume: +6 дБ → 72% сэмплов в потолке). Потребителей у фильтра на
     * тот момент было НОЛЬ — переписан без совместимостных потерь.
     *
     * Теперь: MasteringLimiter.createStreamLimiter (общий true-peak
     * лимитер мастеринга, ниже потолка бит-прозрачен). Он буферизует
     * (lookahead) — выход отстаёт от входа, поэтому фильтр даёт
     * ПЕРЕМЕННУЮ длину чанка и хвост в flush(); конвейер это поддерживает
     * (runChain возвращает массивы, flushChain выпускает хвосты).
     *
     * Без window.MasteringLimiter на странице — прежний жёсткий кламп:
     * НЕ дыра в новой семантике, а сохранение существующих потребителей.
     * ⚠️ «потребителей ноль» оказалось ложью грепа по литералу объекта:
     * /audio-enhancer шлёт alimiter СТРОКАМИ ffmpeg-фильтров, которые
     * парсятся в спеки, и его страница НЕ грузит mastering-limiter.js —
     * гейт в canHandle отобрал у него стрим-маршрут (8+5 красных гардов,
     * 19.08.2026). У энхансера кламп стоит ПОСЛЕ loudnorm TP −1.5 —
     * краевой страховкой, а не рабочей ступенью; перевод энхансера на
     * true-peak — отдельный замер его же гардами, не побочный эффект. */
    function makeLimiter(opts, sampleRate, numChannels) {
        var limit = (opts.limit !== undefined) ? opts.limit : 0.95;
        var ML = (typeof window !== 'undefined') ? window.MasteringLimiter : null;
        if (!(ML && ML.createStreamLimiter)) {
            var negLimit = -limit;
            return {
                process: function (channelArrays) {
                    for (var ch = 0; ch < channelArrays.length; ch++) {
                        var d = channelArrays[ch];
                        for (var i = 0; i < d.length; i++) {
                            var v = d[i];
                            if (v > limit) d[i] = limit;
                            else if (v < negLimit) d[i] = negLimit;
                        }
                    }
                    return channelArrays;
                }
            };
        }
        var ceilingDb = 20 * Math.log10(limit);
        var lim = ML.createStreamLimiter(sampleRate, numChannels, { ceilingDb: ceilingDb });
        function joinBlocks(blocks) {
            if (!blocks || !blocks.length) {
                var empty = [];
                for (var c = 0; c < numChannels; c++) empty.push(new Float32Array(0));
                return empty;
            }
            if (blocks.length === 1) return blocks[0];
            var total = 0, b;
            for (b = 0; b < blocks.length; b++) total += blocks[b][0].length;
            var out = [];
            for (var ch = 0; ch < numChannels; ch++) {
                var arr = new Float32Array(total), off = 0;
                for (b = 0; b < blocks.length; b++) { arr.set(blocks[b][ch], off); off += blocks[b][ch].length; }
                out.push(arr);
            }
            return out;
        }
        return {
            process: function (channelArrays) {
                return joinBlocks(lim.push(channelArrays));
            },
            flush: function () {
                var fin = lim.flush();
                return joinBlocks(fin.blocks);
            }
        };
    }

    // ──────────────────────────────────────────────────────────────────
    //  Polyphase Kaiser-windowed sinc resampler (Stage 5d)
    //
    //  Design rationale: linear interp (Stage 5c, ~−40 dB aliasing at Nyquist)
    //  is fine for noise reduction but introduces audible artefacts on music
    //  with high-frequency content (cymbal shimmer, harmonic edges). Kaiser-
    //  windowed sinc with 32 taps/phase + beta=8.6 gives ~−90 dB stopband —
    //  transparent at 16-bit. State preserved across chunks (sliding delay
    //  line + output position counter) → identical output to whole-signal
    //  processing within float32 precision.
    //
    //  Memory cost: L × tapsPerPhase × 4 bytes per resampler instance. For
    //  44.1 → 48 kHz: L=160, taps=32 → 20 KB. Two resamplers in RNNoise
    //  filter (up + down) = 40 KB. One-time per pipeline construction.
    //
    //  Falls back to linear-interp if the rate ratio decomposes to L > 400
    //  (e.g. odd rates like 11025 → 48000 = 320/73; L=320 still OK; but
    //  some weird rates produce huge L values where Kaiser design becomes
    //  costly without quality gain).
    // ──────────────────────────────────────────────────────────────────

    /** Modified Bessel function of first kind, order 0 — series approximation
     *  good to 1e-12 for |x| < 30 (Kaiser beta values are well within range). */
    function besselI0(x) {
        var sum = 1, term = 1, halfX = x * 0.5;
        for (var k = 1; k < 50; k++) {
            term *= halfX * halfX / (k * k);
            sum += term;
            if (term < 1e-12 * sum) break;
        }
        return sum;
    }

    function kaiserWindow(N, beta) {
        var w = new Float64Array(N);
        var i0Beta = besselI0(beta);
        for (var n = 0; n < N; n++) {
            var x = (2 * n / (N - 1)) - 1;
            w[n] = besselI0(beta * Math.sqrt(1 - x * x)) / i0Beta;
        }
        return w;
    }

    /** Sinc lowpass impulse response, length N, cutoff in [0..0.5] normalized. */
    function designLowpassSinc(N, cutoff) {
        var h = new Float64Array(N);
        var center = (N - 1) / 2;
        for (var n = 0; n < N; n++) {
            var x = n - center;
            if (Math.abs(x) < 1e-12) {
                h[n] = 2 * cutoff;
            } else {
                h[n] = Math.sin(2 * Math.PI * cutoff * x) / (Math.PI * x);
            }
        }
        return h;
    }

    function gcd(a, b) { return b ? gcd(b, a % b) : a; }

    /** Build polyphase decomposition: returns L sub-filters of length tapsPerPhase.
     *  Each sub-filter is REVERSED so streaming convolution is just
     *      sum_{k} win[k] * subFilter[p][k]
     *  with win[N-1] = newest input sample. */
    function designPolyphaseFilter(L, tapsPerPhase, cutoff, beta) {
        var totalLen = L * tapsPerPhase;
        var sinc = designLowpassSinc(totalLen, cutoff);
        var win = kaiserWindow(totalLen, beta);
        var fir = new Float64Array(totalLen);
        // Gain compensate for upsample factor L (otherwise output is L× quieter).
        for (var i = 0; i < totalLen; i++) fir[i] = sinc[i] * win[i] * L;

        var subFilters = [];
        for (var ph = 0; ph < L; ph++) {
            var sf = new Float32Array(tapsPerPhase);
            // Reversed indexing: sf[k] = fir[ph + (tapsPerPhase-1-k)*L]
            // After this, output = sum_{k=0..N-1} win[k] * sf[k] (no further reversal needed).
            for (var k = 0; k < tapsPerPhase; k++) {
                sf[k] = fir[ph + (tapsPerPhase - 1 - k) * L];
            }
            subFilters.push(sf);
        }
        return subFilters;
    }

    /** Cache of designed polyphase filters keyed by "L,M,N,beta". One-time cost. */
    var _polyphaseCache = Object.create(null);

    function makePolyphaseResampler(inputRate, outputRate, opts) {
        opts = opts || {};
        var tapsPerPhase = opts.tapsPerPhase || 32;
        var beta = (opts.beta !== undefined) ? opts.beta : 8.6;
        if (inputRate === outputRate) {
            return { process: function (x) { return x ? new Float32Array(x) : new Float32Array(0); },
                reset: function () {} };
        }
        var g = gcd(inputRate | 0, outputRate | 0);
        var L = (outputRate / g) | 0;
        var M = (inputRate / g) | 0;
        // Sanity bound: L > 400 (very irregular rates) → fall back to linear
        // rather than allocate a 51 KB FIR for marginal quality benefit.
        if (L > 400 || L * tapsPerPhase > 16384) return makeLinearResampler(inputRate, outputRate);

        // Cutoff in normalized form (relative to upsampled-rate Nyquist).
        //   To preserve INPUT bandwidth: cutoff < 1/L
        //     (inputRate/2 in real Hz = upsampled_Nyquist/L)
        //   To prevent aliasing on M-decimation: cutoff < 1/M
        //     (outputRate/2 in real Hz = upsampled_Nyquist*L/(M*L) = upsampled_Nyquist/M)
        //   Use min(1/L, 1/M) at 0.9 to leave a 10 % transition band BELOW
        //   that bound. Earlier (incorrect) form was 0.45/max(L,M) which is
        //   half the correct value — chopped most of the signal bandwidth.
        var cutoff = 0.9 * Math.min(1 / L, 1 / M);
        var cacheKey = L + ',' + M + ',' + tapsPerPhase + ',' + beta;
        var subFilters = _polyphaseCache[cacheKey];
        if (!subFilters) {
            subFilters = designPolyphaseFilter(L, tapsPerPhase, cutoff, beta);
            _polyphaseCache[cacheKey] = subFilters;
        }

        // Streaming state. `win` is a sliding delay line: win[0] = oldest,
        // win[tapsPerPhase-1] = newest input sample.
        var win = new Float64Array(tapsPerPhase);
        var inputCount = 0;
        var outputCount = 0;
        var ratio = L / M;

        return {
            process: function (input) {
                if (!input || input.length === 0) return new Float32Array(0);
                var output = [];
                for (var i = 0; i < input.length; i++) {
                    // Shift delay line: drop oldest, push newest at end
                    for (var k = 0; k < tapsPerPhase - 1; k++) win[k] = win[k + 1];
                    win[tapsPerPhase - 1] = input[i];
                    inputCount++;

                    // Emit any output samples whose required input position is now available.
                    // Output j requires input[floor(j*M/L)] available → j <= (inputCount-1)*L/M
                    var outMax = Math.floor((inputCount - 1) * ratio);
                    while (outputCount <= outMax) {
                        var phase = (outputCount * M) % L;
                        var subFilter = subFilters[phase];
                        var sum = 0;
                        // Reversed sub-filter → straightforward indexed convolution
                        for (var t = 0; t < tapsPerPhase; t++) {
                            sum += win[t] * subFilter[t];
                        }
                        output.push(sum);
                        outputCount++;
                    }
                }
                return new Float32Array(output);
            },
            reset: function () {
                win.fill(0);
                inputCount = 0;
                outputCount = 0;
            }
        };
    }

    /**
     * Streaming linear-interpolation resampler. Maintains phase + residual
     * across calls so chunk boundaries don't introduce discontinuities.
     *
     * For voice-band material (RNNoise input, ≤ 8 kHz fundamentals + harmonics)
     * linear interp aliasing is well below the noise floor we're trying to
     * suppress — quality penalty negligible for noise-reduction purpose.
     * For music or critical playback, polyphase / sinc resamplers (Stage 5d)
     * would be the right upgrade.
     *
     * Algorithm:
     *   step = inputRate / outputRate
     *   For each output sample:
     *     out[i] = combined[floor(phase)] * (1-frac) + combined[floor(phase)+1] * frac
     *     phase += step
     *   After the call: residual = combined.slice(floor(phase)); phase -= floor(phase)
     *
     * Memory: residual holds at most ⌈step⌉ + 1 samples between calls (e.g.
     * 2 samples for 44.1→48 conversion). Negligible.
     */
    function makeLinearResampler(inputRate, outputRate) {
        var step = inputRate / outputRate;
        var phase = 0;
        var residual = new Float32Array(0);
        return {
            process: function (input) {
                if (!input || input.length === 0) return new Float32Array(0);
                // Concatenate residual + input
                var combined;
                if (residual.length === 0) {
                    combined = input;
                } else {
                    combined = new Float32Array(residual.length + input.length);
                    combined.set(residual, 0);
                    combined.set(input, residual.length);
                }
                // Pre-allocate output. Worst case = ⌈combined.length / step⌉.
                var outApprox = Math.ceil((combined.length - phase) / step) + 1;
                var output = new Float32Array(outApprox);
                var nOut = 0;
                while (phase + 1 < combined.length) {
                    var iFloor = phase | 0;        // fast Math.floor for non-negative
                    var frac = phase - iFloor;
                    output[nOut++] = combined[iFloor] * (1 - frac) + combined[iFloor + 1] * frac;
                    phase += step;
                }
                // Compact residual: keep samples from floor(phase) onwards
                var startIdx = phase | 0;
                if (startIdx >= combined.length) {
                    residual = new Float32Array(0);
                    phase -= startIdx;
                } else {
                    residual = combined.slice(startIdx);
                    phase -= startIdx;
                }
                return nOut === outApprox ? output : output.slice(0, nOut);
            },
            // Optional: reset state for reuse. Caller drops residual when re-init needed.
            reset: function () { phase = 0; residual = new Float32Array(0); }
        };
    }

    /**
     * RNNoise — Mozilla recurrent neural-network speech denoiser.
     *
     * Streaming wrapper around the existing /vendor/rnnoise/rnnoise.js WASM
     * module (loaded by the host page via <script src="/vendor/rnnoise/rnnoise.js">).
     * Each call to process(channelArrays):
     *   1. Mono-mixes the chunk (RNNoise operates on mono only)
     *   2. Appends to a 480-sample ring buffer
     *   3. Processes complete 480-sample frames via _rnnoise_process_frame
     *   4. Emits N samples back (oldest first), with initial 480-sample
     *      silence padding for the first chunk (10 ms at 48 kHz — inaudible)
     *   5. Spreads the mono wet signal to all input channels (loses stereo
     *      image, acceptable for speech which is typically centered)
     *
     * VAD-adaptive wet/dry mix mirrors rnnoise-realtime: less suppression
     * during speech (preserve naturalness, ~0.75 wet), full suppression
     * during silence (1.0 wet), smooth transition between.
     *
     * Sample rate handling: RNNoise was trained at 48 kHz with 480-sample
     * (10 ms) frames. The streaming wrapper transparently handles non-48 kHz
     * input via internal up/down resamplers. The `rnnoise` filter accepts any
     * sampleRate and emits at the same rate; caller doesn't have to worry
     * about rate matching.
     *
     * Resampler quality (Stage 5d): Kaiser-windowed sinc polyphase, 32 taps
     * per phase, beta 8.6 → ~−90 dB stopband. Transparent at 16-bit for both
     * voice and music material. Falls back to linear-interp only when the
     * rate ratio decomposes to L > 400 (extreme cases like 11025 → 96000).
     */
    function makeRNNoise(sampleRate, numChannels, mod) {
        if (!mod || typeof mod._rnnoise_create !== 'function') {
            throw new Error('RNNoise WASM module not loaded — include /vendor/rnnoise/rnnoise.js');
        }
        var FRAME = 480;
        var TARGET_SR = 48000;
        var needsResample = sampleRate !== TARGET_SR;
        var upResampler = needsResample ? makePolyphaseResampler(sampleRate, TARGET_SR) : null;
        var downResampler = needsResample ? makePolyphaseResampler(TARGET_SR, sampleRate) : null;

        var state = mod._rnnoise_create();
        var inFramePtr = mod._malloc(FRAME * 4);
        var outFramePtr = mod._malloc(FRAME * 4);

        // Mono ring buffer + processed-frame queue + emit FIFO (at SOURCE rate)
        var inRing = new Float32Array(FRAME);
        var inPos = 0;
        var emitFIFO = new Float32Array(FRAME * 4);  // grows as needed
        var emitFIFOLen = 0;
        var emitFIFOPos = 0;
        var smoothVad = 0.5;
        var samplesEmitted = 0;
        var samplesIngested = 0;

        function pushToFIFO(arr) {
            // Compact remaining FIFO contents to start, then append new data
            if (emitFIFOPos > 0) {
                var remain = emitFIFOLen - emitFIFOPos;
                if (remain > 0) emitFIFO.copyWithin(0, emitFIFOPos, emitFIFOLen);
                emitFIFOLen = remain;
                emitFIFOPos = 0;
            }
            // Grow FIFO if needed
            if (emitFIFOLen + arr.length > emitFIFO.length) {
                var newSize = emitFIFO.length;
                while (newSize < emitFIFOLen + arr.length) newSize *= 2;
                var grown = new Float32Array(newSize);
                grown.set(emitFIFO.subarray(0, emitFIFOLen), 0);
                emitFIFO = grown;
            }
            emitFIFO.set(arr, emitFIFOLen);
            emitFIFOLen += arr.length;
        }

        return {
            process: function (channelArrays) {
                var n = channelArrays[0].length;
                var nCh = channelArrays.length;

                // 1. Mono mix-down for RNNoise input (at SOURCE rate)
                var monoInSrc = new Float32Array(n);
                if (nCh === 1) {
                    monoInSrc.set(channelArrays[0]);
                } else {
                    var inv = 1 / nCh;
                    for (var i = 0; i < n; i++) {
                        var s = 0;
                        for (var ch = 0; ch < nCh; ch++) s += channelArrays[ch][i];
                        monoInSrc[i] = s * inv;
                    }
                }
                samplesIngested += n;

                // 2. Upsample to 48 kHz when source ≠ 48 kHz (Stage 5c).
                // Linear-interp streaming resampler maintains phase across calls
                // → no chunk-boundary discontinuities.
                var monoIn = needsResample ? upResampler.process(monoInSrc) : monoInSrc;

                // 3. Accumulate into 480-frame ring; process via RNNoise; push
                // wet frames into a 48 kHz intermediate FIFO before downsampling.
                var heap = mod.HEAPF32;
                var inIdx = inFramePtr >> 2;
                var outIdx = outFramePtr >> 2;
                var wet48k = new Float32Array(monoIn.length);  // worst case: 1 wet sample per input sample
                var wet48kLen = 0;
                for (var k = 0; k < monoIn.length; k++) {
                    inRing[inPos++] = monoIn[k];
                    if (inPos >= FRAME) {
                        for (var j = 0; j < FRAME; j++) heap[inIdx + j] = inRing[j] * 32768;
                        var vad = mod._rnnoise_process_frame(state, outFramePtr, inFramePtr);
                        smoothVad = smoothVad * 0.85 + vad * 0.15;
                        var strength = (smoothVad > 0.6) ? 0.75
                                     : (smoothVad < 0.15) ? 1.0
                                     : (1.0 - ((smoothVad - 0.15) / 0.45) * 0.25);
                        // Append wet 480-sample frame to the per-call wet48k buffer
                        if (wet48kLen + FRAME > wet48k.length) {
                            var grown = new Float32Array(Math.max(wet48k.length * 2, wet48kLen + FRAME));
                            grown.set(wet48k.subarray(0, wet48kLen));
                            wet48k = grown;
                        }
                        for (var j2 = 0; j2 < FRAME; j2++) {
                            var dry = inRing[j2];
                            var wet = heap[outIdx + j2] / 32768;
                            wet48k[wet48kLen + j2] = wet * strength + dry * (1 - strength);
                        }
                        wet48kLen += FRAME;
                        inPos = 0;
                    }
                }

                // 4. Downsample back to source rate (when needed) and push to source-rate FIFO
                var wetSrc;
                if (needsResample && wet48kLen > 0) {
                    wetSrc = downResampler.process(wet48k.subarray(0, wet48kLen));
                } else if (wet48kLen > 0) {
                    wetSrc = wet48k.subarray(0, wet48kLen);
                } else {
                    wetSrc = new Float32Array(0);
                }
                if (wetSrc.length > 0) pushToFIFO(wetSrc);

                // 5. Emit N source-rate samples from FIFO (zero-pad initial latency
                // while ring buffer + resampler phase fill — typically the first
                // ~10 ms of output is zeros, then real wet signal).
                var emit = new Float32Array(n);
                var avail = emitFIFOLen - emitFIFOPos;
                var copyLen = Math.min(avail, n);
                if (copyLen > 0) {
                    emit.set(emitFIFO.subarray(emitFIFOPos, emitFIFOPos + copyLen), 0);
                    emitFIFOPos += copyLen;
                }
                samplesEmitted += n;

                // 6. Spread mono wet signal to all channels (loses stereo image —
                // acceptable trade for centered speech material)
                for (var ch3 = 0; ch3 < nCh; ch3++) {
                    channelArrays[ch3].set(emit);
                }
                return channelArrays;
            },
            destroy: function () {
                try { mod._rnnoise_destroy(state); } catch (_) {}
                try { mod._free(inFramePtr); } catch (_) {}
                try { mod._free(outFramePtr); } catch (_) {}
            }
        };
    }

    /**
     * stereotools — stateless M/S widener (mid + side decoding).
     *   M = (L + R) / 2 * mlev      (mid level — usually 1)
     *   S = (L - R) / 2 * slev      (side level — > 1 widens, < 1 narrows)
     *   L_out = M + S, R_out = M - S
     * Mono input passes through unchanged.
     */
    function makeStereoTools(opts) {
        var mlev = (opts.mlev !== undefined) ? opts.mlev : 1;
        var slev = (opts.slev !== undefined) ? opts.slev : 1;
        return {
            process: function (channelArrays) {
                if (channelArrays.length < 2) return channelArrays;
                var L = channelArrays[0], R = channelArrays[1];
                for (var i = 0; i < L.length; i++) {
                    var l = L[i], r = R[i];
                    var M = (l + r) * 0.5 * mlev;
                    var S = (l - r) * 0.5 * slev;
                    L[i] = M + S;
                    R[i] = M - S;
                }
                return channelArrays;
            }
        };
    }

    /** Constant gain (used as a streaming-friendly approximation of loudnorm). */
    function makeGain(opts) {
        var dB = (opts.gainDb !== undefined) ? opts.gainDb : (opts.value !== undefined ? opts.value : 0);
        var lin = Math.pow(10, dB / 20);
        return {
            process: function (channelArrays) {
                if (lin === 1) return channelArrays;
                for (var ch = 0; ch < channelArrays.length; ch++) {
                    var d = channelArrays[ch];
                    for (var i = 0; i < d.length; i++) d[i] *= lin;
                }
                return channelArrays;
            }
        };
    }

    /**
     * Огибающая усиления — произвольная кривая, а не константа.
     *
     * Считает НЕ этот файл: точки строит `GainEnvelope` (public/js/gain-envelope.js),
     * он же единственный источник правды по вопросу «какое усиление в момент t».
     * Здесь только состояние потока — номер сэмпла, с которого начинается
     * очередной чанк. Своей интерполяции тут нет намеренно: вторая копия
     * математики и была корнем расхождений превью и файла.
     *
     * Если модуль не загружен — возвращаем null, и `buildChain` честно кладёт
     * фильтр в `opsBlocked`, а вызывающий уйдёт на другой лейн. Тихо пропустить
     * огибающую нельзя: файл уехал бы вообще без обработки.
     */
    function makeEnvelope(opts, sampleRate) {
        var GE = (typeof window !== 'undefined') ? window.GainEnvelope : null;
        if (!GE || !opts || !opts.points || opts.points.length < 2) return null;
        var env = GE.fromPoints(opts.points);
        var samplesProcessed = 0;
        // Частота фиксируется ЗДЕСЬ: chain[i].process() зовут одним
        // аргументом, и подсмотреть её во время прохода неоткуда.
        var sr = sampleRate || 48000;
        return {
            process: function (channelArrays) {
                env.applyToChannels(channelArrays, samplesProcessed, sr);
                samplesProcessed += channelArrays[0].length;
                return channelArrays;
            }
        };
    }

    // Linear fade-in. Matches FFmpeg `afade=t=in:st=0:d=<duration>` and
    // Web Audio's `gain.gain.linearRampToValueAtTime` — same envelope, same
    // boundary samples. Stateful across chunks via `samplesProcessed`; the
    // fast path bails out once we're past the ramp so a 5-hour file pays
    // O(N) only for the ramp window.
    function makeFadeIn(opts, sampleRate) {
        var durationSec = (opts && opts.duration) || 0;
        var fadeSamples = Math.max(1, Math.round(durationSec * sampleRate));
        var samplesProcessed = 0;
        return {
            process: function (channelArrays) {
                if (samplesProcessed >= fadeSamples) {
                    samplesProcessed += channelArrays[0].length;
                    return channelArrays;
                }
                var n = channelArrays[0].length;
                var nCh = channelArrays.length;
                var startPos = samplesProcessed;
                for (var i = 0; i < n; i++) {
                    var pos = startPos + i;
                    if (pos >= fadeSamples) break;
                    var g = pos / fadeSamples;
                    for (var ch = 0; ch < nCh; ch++) channelArrays[ch][i] *= g;
                }
                samplesProcessed += n;
                return channelArrays;
            }
        };
    }

    // Linear fade-out. Needs the caller to pass `total` (seconds) up front so
    // the filter knows where the ramp starts. Matches FFmpeg
    // `afade=t=out:st=<start>:d=<duration>`. If `total` is missing the filter
    // is a no-op (graceful degradation rather than a thrown error mid-stream).
    function makeFadeOut(opts, sampleRate) {
        var durationSec = (opts && opts.duration) || 0;
        var totalSec = (opts && opts.total) || 0;
        var fadeSamples = Math.max(1, Math.round(durationSec * sampleRate));
        var totalSamples = Math.round(totalSec * sampleRate);
        var fadeStart = Math.max(0, totalSamples - fadeSamples);
        var disabled = totalSec <= 0 || durationSec <= 0;
        var samplesProcessed = 0;
        return {
            process: function (channelArrays) {
                if (disabled) {
                    samplesProcessed += channelArrays[0].length;
                    return channelArrays;
                }
                var n = channelArrays[0].length;
                if (samplesProcessed + n <= fadeStart) {
                    samplesProcessed += n;
                    return channelArrays;
                }
                var nCh = channelArrays.length;
                var startPos = samplesProcessed;
                for (var i = 0; i < n; i++) {
                    var pos = startPos + i;
                    if (pos < fadeStart) continue;
                    var d = pos - fadeStart;
                    var g = (d >= fadeSamples) ? 0 : (1 - d / fadeSamples);
                    for (var ch = 0; ch < nCh; ch++) channelArrays[ch][i] *= g;
                }
                samplesProcessed += n;
                return channelArrays;
            }
        };
    }

    /**
     * Build a chain of stateful filters from a parsed spec list. Returns
     * { chain: [...], opsApplied: [...], opsBlocked: [...] }.
     *
     * `extras` (optional) carries dependencies that some filters need:
     *   - rnnoiseModule: WASM module instance from createRNNWasmModule(),
     *     required when chain contains 'rnnoise'.
     */
    function buildChain(filterSpecs, sampleRate, numChannels, extras) {
        var chain = [];
        var opsApplied = [];
        var opsBlocked = [];
        for (var i = 0; i < filterSpecs.length; i++) {
            var f = filterSpecs[i];
            switch (f.name) {
                case 'highpass': {
                    chain.push(makeBiquad('highpass', sampleRate, f.f || 120, 0.7071, 0, numChannels));
                    opsApplied.push('highpass@' + (f.f || 120));
                    break;
                }
                case 'lowpass': {
                    chain.push(makeBiquad('lowpass', sampleRate, f.f || 10000, 0.7071, 0, numChannels));
                    opsApplied.push('lowpass@' + (f.f || 10000));
                    break;
                }
                case 'equalizer': {
                    var freq = f.f || 1000;
                    var Q = (f.w && freq > 0) ? freq / f.w : 1.0;
                    chain.push(makeBiquad('peaking', sampleRate, freq, Q, f.g || 0, numChannels));
                    opsApplied.push('eq@' + freq + (f.g >= 0 ? '+' : '') + (f.g || 0));
                    break;
                }
                case 'acompressor': {
                    chain.push(makeCompressor(f, sampleRate));
                    opsApplied.push('comp@' + (f.threshold || -20) + 'dB×' + (f.ratio || 3));
                    break;
                }
                case 'alimiter': {
                    chain.push(makeLimiter(f, sampleRate, numChannels));
                    opsApplied.push('limit@' + (f.limit || 0.95));
                    break;
                }
                case 'gain': {
                    chain.push(makeGain(f));
                    opsApplied.push('gain@' + (f.gainDb || 0) + 'dB');
                    break;
                }
                case 'envelope': {
                    // Обобщение gain/fadeIn/fadeOut: произвольная кривая
                    // усиления во времени. Именно из-за её отсутствия участки
                    // /volume дисквалифицировали этот лейн и уезжали на
                    // FFmpeg-WASM — самый тяжёлый путь из четырёх (17% всех
                    // экспортов при 73% у стриминга).
                    var envF = makeEnvelope(f, sampleRate);
                    if (!envF) { opsBlocked.push('envelope (gain-envelope.js not loaded)'); break; }
                    chain.push(envF);
                    opsApplied.push('envelope@' + ((f.points && f.points.length) || 0) + 'pts');
                    break;
                }
                case 'fadeIn': {
                    chain.push(makeFadeIn(f, sampleRate));
                    opsApplied.push('fadeIn@' + (f.duration || 0) + 's');
                    break;
                }
                case 'fadeOut': {
                    chain.push(makeFadeOut(f, sampleRate));
                    opsApplied.push('fadeOut@' + (f.duration || 0) + 's');
                    break;
                }
                case 'stereotools': {
                    chain.push(makeStereoTools(f));
                    opsApplied.push('stereotools@slev=' + (f.slev || 1));
                    break;
                }
                case 'rnnoise': {
                    // RNNoise needs the WASM module passed in via opts —
                    // buildChain caller must supply it when the chain
                    // contains rnnoise. See process() init-flow below.
                    if (!extras || !extras.rnnoiseModule) {
                        opsBlocked.push('rnnoise (WASM module not loaded)');
                        break;
                    }
                    chain.push(makeRNNoise(sampleRate, numChannels, extras.rnnoiseModule));
                    opsApplied.push('rnnoise@48kHz');
                    break;
                }
                default: {
                    opsBlocked.push(f.name);
                    break;
                }
            }
        }
        return { chain: chain, opsApplied: opsApplied, opsBlocked: opsBlocked };
    }

    function runChain(chain, channelArrays) {
        for (var i = 0; i < chain.length; i++) {
            channelArrays = chain[i].process(channelArrays);
        }
        return channelArrays;
    }

    /* Хвосты фильтров с lookahead (лимитер буферизует и отдаёт с задержкой):
       каждый flush() прогоняется через ОСТАТОК цепочки, чтобы хвост лимитера
       прошёл через фильтры, стоящие после него. Фильтры без flush — пропуск. */
    function flushChain(chain, numChannels) {
        var out = null;
        for (var i = 0; i < chain.length; i++) {
            if (typeof chain[i].flush !== 'function') continue;
            var t = chain[i].flush();
            if (!t || !t[0] || !t[0].length) continue;
            for (var j = i + 1; j < chain.length; j++) t = chain[j].process(t);
            if (!t || !t[0] || !t[0].length) continue;
            if (!out) { out = t; continue; }
            var joined = [];
            for (var c = 0; c < numChannels; c++) {
                var arr = new Float32Array(out[c].length + t[c].length);
                arr.set(out[c], 0); arr.set(t[c], out[c].length);
                joined.push(arr);
            }
            out = joined;
        }
        return out;
    }

    // ──────────────────────────────────────────────────────────────────
    //  WAV input/output streaming
    // ──────────────────────────────────────────────────────────────────

    /**
     * Parse a RIFF/WAVE header from the first ~64 bytes. Returns
     * { sampleRate, numChannels, bitsPerSample, dataOffset, dataSize, pcmFormat }.
     * Supports linear PCM (fmt=1) and IEEE float (fmt=3). Throws on anything else.
     */
    function readWavHeader(bytes) {
        if (bytes.length < 44) throw new Error('WAV: header too short');
        if (bytes[0] !== 0x52 || bytes[1] !== 0x49 || bytes[2] !== 0x46 || bytes[3] !== 0x46) {
            throw new Error('WAV: missing RIFF magic');
        }
        if (bytes[8] !== 0x57 || bytes[9] !== 0x41 || bytes[10] !== 0x56 || bytes[11] !== 0x45) {
            throw new Error('WAV: missing WAVE magic');
        }
        var dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        var off = 12;
        var fmtId = 0, sampleRate = 0, numChannels = 0, bitsPerSample = 0;
        var dataOffset = 0, dataSize = 0, fmtBody = 0, fmtChunkSize = 0;
        while (off + 8 <= bytes.length) {
            var chunkId = String.fromCharCode(bytes[off], bytes[off+1], bytes[off+2], bytes[off+3]);
            var chunkSize = dv.getUint32(off + 4, true);
            if (chunkId === 'fmt ') {
                fmtId = dv.getUint16(off + 8, true);
                numChannels = dv.getUint16(off + 10, true);
                sampleRate = dv.getUint32(off + 12, true);
                bitsPerSample = dv.getUint16(off + 22, true);
                fmtBody = off + 8; fmtChunkSize = chunkSize;
            } else if (chunkId === 'data') {
                dataOffset = off + 8;
                dataSize = chunkSize;
                break;
            }
            off += 8 + chunkSize + (chunkSize & 1);
        }
        // WAVE_FORMAT_EXTENSIBLE (0xFFFE) carries the REAL format in the first
        // two bytes of its SubFormat GUID, 24 bytes into the fmt body. This is
        // what ffmpeg and most DAWs write for 24-bit and for anything above two
        // channels, so rejecting it turned every ordinary 24-bit wav away from
        // the streaming route and onto the memory-hungry fallback. The sample
        // reader below already handled 24-bit PCM correctly — only this gate
        // stood in the way. Found by measurement 2026-07-31.
        if (fmtId === 65534 && fmtChunkSize >= 40) fmtId = dv.getUint16(fmtBody + 24, true);
        if (fmtId !== 1 && fmtId !== 3) throw new Error('WAV: only PCM (fmt=1) and IEEE float (fmt=3) supported (got ' + fmtId + ')');
        return { sampleRate: sampleRate, numChannels: numChannels, bitsPerSample: bitsPerSample,
            dataOffset: dataOffset, dataSize: dataSize, pcmFormat: fmtId };
    }

    /**
     * Stream-decode a WAV blob: parses header, then yields chunks of
     * `samplesPerChunk` samples to the onChunk callback. Memory: O(chunk).
     */
    async function decodeWavStream(blob, opts) {
        var headerSlice = blob.slice(0, Math.min(blob.size, 4096));
        var headerBytes = new Uint8Array(await headerSlice.arrayBuffer());
        var hdr = readWavHeader(headerBytes);
        var bytesPerSample = hdr.bitsPerSample / 8;
        var bytesPerFrame = bytesPerSample * hdr.numChannels;
        var samplesPerChunk = opts.samplesPerChunk || 4096;
        var chunkBytes = samplesPerChunk * bytesPerFrame;

        var pos = hdr.dataOffset;
        var endPos = hdr.dataOffset + hdr.dataSize;
        var totalFrames = Math.floor(hdr.dataSize / bytesPerFrame);
        var framesYielded = 0;

        while (pos < endPos) {
            var sliceEnd = Math.min(pos + chunkBytes, endPos);
            var slice = blob.slice(pos, sliceEnd);
            var buf = new Uint8Array(await slice.arrayBuffer());
            var dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
            var nFrames = Math.floor(buf.length / bytesPerFrame);
            var channelData = [];
            for (var ch = 0; ch < hdr.numChannels; ch++) channelData.push(new Float32Array(nFrames));
            for (var f = 0; f < nFrames; f++) {
                for (var ch2 = 0; ch2 < hdr.numChannels; ch2++) {
                    var sampleOff = f * bytesPerFrame + ch2 * bytesPerSample;
                    var v;
                    if (hdr.pcmFormat === 1) {
                        if (hdr.bitsPerSample === 16) v = dv.getInt16(sampleOff, true) / 32768;
                        // 8-bit wav is UNSIGNED with a 128 offset — the one PCM
                        // depth that is not two's complement. 29 fallthroughs in
                        // 90 days of /volume telemetry came from this line
                        // throwing instead.
                        else if (hdr.bitsPerSample === 8) v = (buf[sampleOff] - 128) / 128;
                        else if (hdr.bitsPerSample === 24) {
                            // 24-bit signed PCM, little-endian, sign-extend
                            var b0 = buf[sampleOff], b1 = buf[sampleOff+1], b2 = buf[sampleOff+2];
                            var raw = b0 | (b1 << 8) | (b2 << 16);
                            if (raw & 0x800000) raw |= 0xFF000000;
                            v = raw / 8388608;
                        } else if (hdr.bitsPerSample === 32) v = dv.getInt32(sampleOff, true) / 2147483648;
                        else throw new Error('WAV: unsupported bit depth ' + hdr.bitsPerSample);
                    } else {
                        v = dv.getFloat32(sampleOff, true);
                    }
                    channelData[ch2][f] = v;
                }
            }
            await opts.onChunk({ channelData: channelData, sampleRate: hdr.sampleRate, numberOfFrames: nFrames });
            framesYielded += nFrames;
            if (typeof opts.onProgress === 'function' && totalFrames > 0) {
                try { opts.onProgress(framesYielded / totalFrames); } catch (_) {}
            }
            pos = sliceEnd;
        }
        return { framesDecoded: framesYielded, sampleRate: hdr.sampleRate, channels: hdr.numChannels };
    }

    /**
     * Streaming WAV writer to OutputSink.
     *
     * The implementation moved to audio-output-sink.js on 2026-08-29, next to
     * the sink it writes into: /channel-converter needs the writer and none of
     * this module's 86 KB of mastering DSP, and the only alternative to sharing
     * was a second copy of the same 60 lines. This name stays because
     * mastering-stream.js calls it.
     */
    function makeWavWriter(sink, sampleRate, numChannels, bitsPerSample) {
        if (!(window.OutputSink && window.OutputSink.wavWriter)) {
            throw new Error('OutputSink.wavWriter unavailable — audio-output-sink.js must be loaded');
        }
        return window.OutputSink.wavWriter(sink, sampleRate, numChannels, bitsPerSample);
    }

    // ──────────────────────────────────────────────────────────────────
    //  Eligibility check + main process()
    // ──────────────────────────────────────────────────────────────────

    function getInputExt(file) {
        var name = (file && file.name) || '';
        return (name.split('.').pop() || '').toLowerCase();
    }

    function canHandle(filterSpecs, inputExt, outputFormat) {
        var inExt = (inputExt || '').toLowerCase();
        var outFmt = (outputFormat || '').toLowerCase();
        var opsSupported = [], opsBlocked = [];
        for (var i = 0; i < (filterSpecs || []).length; i++) {
            var name = filterSpecs[i].name;
            if (STREAMING_FILTERS.indexOf(name) !== -1) opsSupported.push(name);
            else opsBlocked.push(name);
        }
        if (STREAMING_FORMATS.indexOf(inExt) === -1) {
            return { eligible: false, reason: 'input_format_unsupported',
                opsSupported: opsSupported, opsBlocked: opsBlocked };
        }
        if (STREAMING_FORMATS.indexOf(outFmt) === -1) {
            return { eligible: false, reason: 'output_format_unsupported',
                opsSupported: opsSupported, opsBlocked: opsBlocked };
        }
        if (opsBlocked.length > 0) {
            return { eligible: false, reason: 'filter_unsupported',
                opsSupported: opsSupported, opsBlocked: opsBlocked };
        }
        return { eligible: true, reason: 'ok',
            opsSupported: opsSupported, opsBlocked: opsBlocked };
    }

    /**
     * Run the streaming pipeline. Caller must verify eligibility via canHandle
     * BEFORE calling process — process() throws on unsupported configurations.
     */
    async function process(file, opts) {
        opts = opts || {};
        var inputExt = getInputExt(file);
        var outputFormat = (opts.output && opts.output.format) || 'wav';
        var slug = opts.slug || 'unknown';
        var onProgress = opts.onProgress || function () {};
        var filterSpecs = opts.filters || [];
        var elig = canHandle(filterSpecs, inputExt, outputFormat);
        if (!elig.eligible) throw new Error('AudioStreamPipeline: not eligible — ' + elig.reason);

        // RNNoise WASM init when the chain calls for it. The module
        // (`createRNNWasmModule`) is loaded by host page via
        // <script src="/vendor/rnnoise/rnnoise.js">; we await its instantiation
        // here so the filter has a ready handle by build time.
        var rnnoiseModule = null;
        var needsRNNoise = filterSpecs.some(function (f) { return f.name === 'rnnoise'; });

        var sink = null;        // built inside the guarded section below
        var chain = null;       // built lazily once we know sampleRate/channels
        var encoder = null;     // built lazily for MP3
        var wavWriter = null;   // built lazily for WAV
        var firstSampleRate = 0, firstChannels = 0;
        var startTs = Date.now();

        var bitrate = (opts.output && opts.output.bitrate) || 192;

        // Cancellation: caller may pass an AbortSignal (window.__createCancellation().signal).
        // Abort is checked at every chunk boundary and before finalisation; on
        // trip the decoder loop terminates via thrown AbortError, the catch
        // block cleans up the MP3 encoder worker session + the OPFS sink.
        // Without this, clicking Cancel during a streaming run hid the
        // progress bar but the pipeline kept burning CPU to completion in the
        // background (verified 2026-05-19: 79 % of audio-enhancer runs use
        // this path; none were cancellable before this change).
        var signal = opts.signal || null;
        function _abortIfSignaled() {
            if (signal && signal.aborted) {
                var reason = signal.reason;
                if (reason && (reason instanceof Error || (reason && reason.name === 'AbortError'))) throw reason;
                throw new DOMException(
                    (typeof reason === 'string' && reason) || 'AudioStreamPipeline aborted',
                    'AbortError'
                );
            }
        }

        // Stall watchdog — bug #609 (Svetlana, 2026-06-02 + 51-session 14d
        // freeze cohort). The MP3Encoder watchdog covers worker-level silence
        // but doesn't see WHICH pipeline stage is stuck; the dec.flush /
        // Promise.all(pendingPushes) chain inside Mp3DecoderStream can sit
        // forever on a single never-resolving feed() if the per-call
        // deadline misfires too. This is the LAST line of defence: if NO
        // chunk arrives for PIPELINE_STALL_MS, declare the pipeline stalled,
        // emit telemetry, and REJECT THE OUTER AWAIT via Promise.race so the
        // caller falls back to the FFmpeg WASM path. Refreshed by every onChunk.
        // Two-stage stall watchdog — bug #609 polish (2026-06-03 PM). At
        // SLOW_HINT_MS we emit a non-fatal `pipeline_slow{stage}` so the
        // caller's onProgress can change the UI text (e.g. "Engine slow,
        // will switch to FFmpeg if no progress") and the user sees informed
        // waiting instead of confused frozen-bar. At PIPELINE_STALL_MS we
        // hard-abort and fall through to FFmpeg. Adobe Podcast / Descript
        // follow the same UX pattern: spinner → "still working…" at 10 s →
        // real action at 20–45 s.
        var PIPELINE_STALL_MS = 45000;
        var SLOW_HINT_MS = 10000;
        var stallTimeoutMs = Number(window.__PIPELINE_STALL_OVERRIDE) || PIPELINE_STALL_MS;
        var slowHintMs = Number(window.__PIPELINE_SLOW_HINT_OVERRIDE) || SLOW_HINT_MS;
        var lastTick = Date.now();
        var stalledAt = null;
        var slowHintFired = false;
        var stallStage = 'init';
        var stallReject = null;
        var stallPromise = new Promise(function (_resolve, reject) { stallReject = reject; });
        // Attach a no-op .catch so an unobserved rejection (caller hits its
        // own error first) doesn't fire onunhandledrejection. The real
        // rejection still propagates through Promise.race to the outer await.
        stallPromise.catch(function () {});
        function _checkStall(stage) {
            stallStage = stage || stallStage;
            lastTick = Date.now();
            stalledAt = null;
            slowHintFired = false;
        }
        var stallTimer = setInterval(function () {
            var elapsed = Date.now() - lastTick;
            // SLOW HINT phase — non-fatal, just lets the UI inform the user.
            // Fires ONCE per stall window (resets on _checkStall).
            if (!slowHintFired && elapsed > slowHintMs) {
                slowHintFired = true;
                _ae('pipeline_slow', {
                    slug: slug, stage: stallStage,
                    slow_ms: elapsed,
                    sinkMode: (sink && sink.mode) || 'unknown',
                    hidden: (typeof document !== 'undefined' && document.hidden) ? 1 : 0,
                });
                // Push a "slow" signal through onProgress so the host page can
                // swap the progress label without polling our private state.
                try { onProgress(0.95, 'slow'); } catch (_) {}
            }
            // HARD STALL phase — abort the pipeline. Emit canonical
            // `tool_error{error_type:'pipeline_stalled'}` per [reference_telemetry_single_emitter]
            // — the bespoke top-level event has been folded in.
            if (!stalledAt && elapsed > stallTimeoutMs) {
                stalledAt = Date.now();
                var err = new Error('AudioStreamPipeline: pipeline stalled at ' + stallStage
                    + ' (no progress for ' + Math.round(elapsed / 1000) + 's)');
                err.code = 'PIPELINE_STALLED';
                err.stage = stallStage;
                err.stalledMs = elapsed;
                if (stallReject) { stallReject(err); stallReject = null; }
                clearInterval(stallTimer);
            }
        }, 2000);
        // Per-stage deadline overrides — Playwright tests can tighten any
        // single deadline via `window.__PIPELINE_<STAGE>_DEADLINE_OVERRIDE`.
        function _stageDeadlineMs(stage, defaultMs) {
            var key = '__PIPELINE_' + stage.toUpperCase() + '_DEADLINE_OVERRIDE';
            return Number(window[key]) || defaultMs;
        }
        function _withDeadline(promise, defaultMs, stage) {
            // For finalisation stages (encoder.finish / sink.finish /
            // wavWriter.finalize) we race against a dedicated timer so a
            // never-resolving I/O await surfaces as a typed error. Rejection
            // carries `code: 'PIPELINE_STALLED'` so the catch block below
            // emits the single canonical `tool_error{error_type:'pipeline_stalled'}`
            // — no double-emission.
            var ms = _stageDeadlineMs(stage, defaultMs);
            return new Promise(function (resolve, reject) {
                var timer = setTimeout(function () {
                    var err = new Error('AudioStreamPipeline: ' + stage + ' deadline (' + ms + 'ms)');
                    err.code = 'PIPELINE_STALLED';
                    err.stage = stage;
                    err.stalledMs = ms;
                    reject(err);
                }, ms);
                promise.then(function (v) { clearTimeout(timer); resolve(v); },
                             function (e) { clearTimeout(timer); reject(e); });
            });
        }

        // Track maximum input |sample| across all decoded chunks. Used by the
        // OutputIntegrity post-finish check (silent-output guard) — knowing
        // the input was audible is the only way to call the output "silent
        // corruption" instead of "user fed silence in, got silence out".
        // Cost is one fma-cmp per sample, dominated by the filter chain that
        // already touches the same memory.  Bug surfaced 2026-06-12 by an
        // anonymous Yandex feedback: lamejs 64 kbps + 44.1 kHz + stereo
        // produces a valid-bytes mp3 whose contents decode to digital
        // silence (-90 dBFS).  FFmpeg CLI at the same config produces a
        // clean -10 dBFS mp3 — it is a lamejs JS-port limitation, not LAME.
        var maxInputPeak = 0;
        var totalInputSamples = 0;
        try {
            // ── Setup ────────────────────────────────────────────────────
            // Everything below used to run BEFORE the watchdog was armed and
            // outside this try, so a stage that neither resolved nor rejected
            // left the caller awaiting forever with no telemetry, no fallback
            // and no way out but reloading the page. Measured 2026-08-11 by
            // hanging each await in turn: `ensureAudioPipeline`, the sink's
            // `getFileHandle` and its `createWritable` each produced an
            // infinite spinner on /volume whose telemetry stopped dead after
            // `pipeline_route` — the exact signature of the bug report that
            // session (six exports, six spinners, six Cancels). A hang one
            // step later, in the decode loop, recovered in 46 s because the
            // watchdog covered it. The stages are not differently risky; only
            // differently guarded. Each deadline stays under PIPELINE_STALL_MS
            // so the two mechanisms cannot fight over the same stall.
            _checkStall('ensure_pipeline');
            if (typeof window.ensureAudioPipeline === 'function') {
                await _withDeadline(Promise.resolve(window.ensureAudioPipeline()), 25000, 'ensure_pipeline');
            }

            if (needsRNNoise) {
                _checkStall('rnnoise_init');
                if (typeof window.RNNoiseRealtime === 'undefined' || typeof window.RNNoiseRealtime.init !== 'function') {
                    throw new Error('AudioStreamPipeline: rnnoise filter requires RNNoiseRealtime to be loaded (include /js/rnnoise-realtime.js)');
                }
                var ok = await _withDeadline(Promise.resolve(window.RNNoiseRealtime.init()), 40000, 'rnnoise_init');
                if (!ok) throw new Error('AudioStreamPipeline: RNNoise WASM init failed (loader not found / WASM unsupported)');
                // Pluck the underlying module through a tiny accessor — RNNoiseRealtime
                // holds it privately, but createNode would expose it. Easier: re-load
                // via createRNNWasmModule which is idempotent across the same page.
                if (typeof window.createRNNWasmModule === 'undefined') {
                    throw new Error('AudioStreamPipeline: createRNNWasmModule undefined after RNNoiseRealtime.init()');
                }
                rnnoiseModule = await _withDeadline(Promise.resolve(window.createRNNWasmModule({
                    locateFile: function (path) { return '/vendor/rnnoise/' + path; }
                })), 40000, 'rnnoise_init');
            }

            // Pick decoder based on input ext
            var decoder;
            if (inputExt === 'mp3') {
                if (!window.Mp3DecoderStream) throw new Error('AudioStreamPipeline: Mp3DecoderStream unavailable');
                decoder = window.Mp3DecoderStream;
            } else if (inputExt === 'wav') {
                decoder = { isSupported: function () { return true; }, decode: decodeWavStream };
            } else {
                throw new Error('AudioStreamPipeline: input ext ' + inputExt + ' not implemented');
            }

            // Prepare sink (RAM until the output outgrows OutputSink.SPILL_AT)
            if (!window.OutputSink) throw new Error('AudioStreamPipeline: OutputSink unavailable');
            _checkStall('sink_create');
            var sinkMime = outputFormat === 'mp3' ? 'audio/mpeg' : 'audio/wav';
            sink = await _withDeadline(
                Promise.resolve(window.OutputSink.create({ mime: sinkMime, name: slug + '-stream' })),
                10000, 'sink_create'
            );

            // Own stage marker: the per-chunk _checkStall below cannot run
            // until the FIRST chunk arrives, so without this a hang in the
            // decoder's opening read is filed under whatever stage happened to
            // run last ('sink_create') and sends the next reader to the wrong
            // place.
            _checkStall('decode_start');
            var decodeRes = await Promise.race([stallPromise, decoder.decode(file, {
                onChunk: async function (s) {
                    _abortIfSignaled();
                    _checkStall(chain === null ? 'first_chunk' : 'chunks');
                    var sr = s.sampleRate;
                    var ch = s.channelData.length;
                    if (chain === null) {
                        firstSampleRate = sr;
                        firstChannels = ch;
                        // Stage 5c: rnnoise filter accepts any sampleRate
                        // (auto-resamples internally). The earlier 48 kHz
                        // throw is gone; chunk processing is rate-agnostic.
                        var built = buildChain(filterSpecs, sr, ch, { rnnoiseModule: rnnoiseModule });
                        chain = built.chain;
                        chain._opsApplied = built.opsApplied;
                        chain._opsBlocked = built.opsBlocked;
                        if (outputFormat === 'mp3') {
                            if (!window.MP3Encoder || !window.MP3Encoder.encodeStream) {
                                throw new Error('AudioStreamPipeline: MP3Encoder.encodeStream unavailable');
                            }
                            encoder = await window.MP3Encoder.encodeStream({
                                sampleRate: sr,
                                channels: Math.min(ch, 2),
                                bitrate: bitrate,
                                slug: slug,
                                accumulate: false
                            });
                        } else {
                            wavWriter = await makeWavWriter(sink, sr, ch, 16);
                        }
                    }
                    totalInputSamples += s.numberOfFrames || (s.channelData[0] && s.channelData[0].length) || 0;
                    // Лимитер с lookahead буферизует — выход runChain может
                    // быть КОРОЧЕ входа (хвост придёт в flushChain ниже).
                    var processed = runChain(chain, s.channelData);
                    if (!processed || !processed[0] || !processed[0].length) return;
                    // Track peak BEFORE feeding the encoder (which transfers
                    // the buffers and detaches them on this thread). One
                    // pass per sample, cache-warm with the filter chain.
                    for (var _ch = 0; _ch < processed.length; _ch++) {
                        var _arr = processed[_ch];
                        for (var _i = 0; _i < _arr.length; _i++) {
                            var _v = _arr[_i];
                            if (_v < 0) _v = -_v;
                            if (_v > maxInputPeak) maxInputPeak = _v;
                        }
                    }
                    if (encoder) {
                        var encChunk = await encoder.feed(processed);
                        if (encChunk && encChunk.byteLength > 0) await sink.write(encChunk);
                    } else if (wavWriter) {
                        await wavWriter.writeChunk(processed);
                    }
                },
                onProgress: function (r) { try { onProgress(Math.min(0.95, r * 0.95), 'process'); } catch (_) {} }
            })]);

            _abortIfSignaled();
            // Хвосты lookahead-фильтров (лимитер отдаёт последний блок только
            // на flush) — доехать в выход ДО finish() энкодера/writer'а.
            if (chain && chain.length) {
                var chainTail = flushChain(chain, firstChannels);
                if (chainTail && chainTail[0] && chainTail[0].length) {
                    if (encoder) {
                        var tailEnc = await encoder.feed(chainTail);
                        if (tailEnc && tailEnc.byteLength > 0) await sink.write(tailEnc);
                    } else if (wavWriter) {
                        await wavWriter.writeChunk(chainTail);
                    }
                }
            }
            var finalBlob;
            if (encoder) {
                _checkStall('encoder_finish');
                // encoder.finish() already has its own 15s per-call deadline
                // (mp3-encoder.js); _withDeadline here is a backstop at 30s
                // covering a hypothetical case where the worker-side timer
                // mis-fires under throttling.
                var tail = await _withDeadline(encoder.finish(), 30000, 'encoder_finish');
                // sink_tail_write deadline lifted 15→30s: a low-bitrate 4-hour
                // audiobook can produce a multi-MB tail, and OPFS write on a
                // slow eMMC has been observed >10s. 30s gives the same generous
                // margin as sink_finish.
                if (tail.tail && tail.tailBytes > 0) await _withDeadline(sink.write(tail.tail), 30000, 'sink_tail_write');
                _checkStall('sink_finish');
                finalBlob = await _withDeadline(sink.finish(), 30000, 'sink_finish');
                // the encoder's Info frame (its own delay and padding) goes in front of the music
                finalBlob = window.OutputSink.prepend(finalBlob, tail && tail.head);
            } else {
                _checkStall('wav_finalize');
                finalBlob = await _withDeadline(wavWriter.finalize(), 30000, 'wav_finalize');
            }

            clearInterval(stallTimer);

            // OutputIntegrity post-finish guard.  Reuses the project-wide
            // primitive (public/js/output-integrity.js) so every encoder
            // bug class — silent frames, truncation, bloat, undecodable
            // container — surfaces through the SAME typed signal regardless
            // of which engine produced it.  Closes feedback 2026-06-12
            // (anonymous Yandex, 65 KB mp3 + +11.5 dB → silent mp3 output)
            // and structurally guards against future encoder regressions.
            // The check is best-effort: if the verification decode itself
            // fails we don't break a happy flow; only an UNAMBIGUOUS
            // {corrupt:true, reason:'silent_output'|'truncated'|'bloated'}
            // result throws.
            if (typeof window.OutputIntegrity !== 'undefined' && finalBlob && finalBlob.size > 0) {
                var verifyAc = null;
                try {
                    verifyAc = new (window.AudioContext || window.webkitAudioContext)();
                    var afterBuf = await window.OutputIntegrity.decodeForCheck(finalBlob, verifyAc);
                    // beforeStub: a synthetic AudioBuffer-like that carries
                    // duration + the streaming-tracked peak through OI's
                    // peakOf() so we don't pay for a redundant whole-file
                    // decode of the input.  OI accepts ANY object with a
                    // getChannelData() that returns a Float32Array — we
                    // provide a 1-sample channel holding maxInputPeak.
                    var beforeDur = (firstSampleRate > 0)
                        ? (totalInputSamples / firstSampleRate)
                        : 0;
                    var beforeStub = {
                        duration: beforeDur,
                        numberOfChannels: 1,
                        getChannelData: function () { return new Float32Array([maxInputPeak]); }
                    };
                    var chk = window.OutputIntegrity.check(beforeStub, afterBuf, {
                        // Streaming pipeline currently applies sample-count
                        // preserving ops only (gain / fade / EQ / dynamics).
                        // Any duration drift > 0.5 s OR > 3 % is a real
                        // corruption signal, not a legitimate edit.
                        expectDurationChange: false
                    });
                    if (chk.corrupt && chk.reason !== 'decode_failed') {
                        _ae('pipeline_integrity_failed', {
                            slug: slug, ext: inputExt, fmt: outputFormat,
                            reason: chk.reason,
                            bitrate: bitrate,
                            sample_rate: firstSampleRate,
                            channels: firstChannels,
                            input_peak: +maxInputPeak.toFixed(4),
                            output_peak: isFinite(chk.afterPeak) ? +chk.afterPeak.toFixed(6) : 0,
                            before_dur: +beforeDur.toFixed(2),
                            after_dur: +chk.afterDur.toFixed(2),
                            sinkMode: sink.mode || 'unknown'
                        });
                        var err = new Error('AudioStreamPipeline: output integrity ' +
                            chk.reason + ' (input peak=' + maxInputPeak.toFixed(4) +
                            ' → output peak=' + (isFinite(chk.afterPeak) ? chk.afterPeak.toExponential(2) : 'NaN') + ')');
                        err.code = 'PIPELINE_INTEGRITY_FAILED';
                        err.reason = chk.reason;
                        throw err;
                    }
                } catch (oiErr) {
                    if (oiErr && oiErr.code === 'PIPELINE_INTEGRITY_FAILED') {
                        // Let the finally close verifyAc once; don't double
                        // close (Chromium throws InvalidStateError on the
                        // second .close()).  Rethrow so the caller falls
                        // through to the WASM path.
                        throw oiErr;
                    }
                    // Verification-internal failure — log but don't break a
                    // happy flow.  The encoder produced bytes; OutputIntegrity
                    // just couldn't independently verify them.  Common cause:
                    // sandbox AudioContext close races, memory pressure on
                    // the second decode.  Counted in telemetry separately so
                    // we can spot if it ever becomes systemic.
                    try {
                        _ae('pipeline_integrity_check_skipped', {
                            slug: slug, err: ((oiErr && oiErr.message) || String(oiErr)).slice(0, 120)
                        });
                    } catch (_) {}
                } finally {
                    try { verifyAc && verifyAc.close(); } catch (_) {}
                }
            }

            var name = (file.name || 'audio').replace(/\.[^.]+$/, '') + '.' + outputFormat;
            var url;
            try { url = URL.createObjectURL(finalBlob); } catch (_) { url = ''; }
            var ms = Date.now() - startTs;

            _ae('audio_stream_pipeline_ok', {
                slug: slug, ext: inputExt, fmt: outputFormat,
                ms: ms, fileMB: Math.round(file.size / 1048576),
                outMB: Math.round(finalBlob.size / 1048576),
                opsApplied: chain._opsApplied.length,
                opsBlocked: chain._opsBlocked.length,
                sinkMode: sink.mode || 'unknown'
            });
            return {
                blob: finalBlob, name: name, url: url, size: finalBlob.size,
                mode: 'streaming',
                sampleRate: firstSampleRate, channels: firstChannels,
                opsApplied: chain._opsApplied, opsBlocked: chain._opsBlocked,
                durationMs: ms
            };
        } catch (e) {
            clearInterval(stallTimer);
            // Release the MP3 worker session so the next encode starts fresh;
            // synchronous, no await needed (worker.postMessage('stream-abort')).
            if (encoder && typeof encoder.abort === 'function') {
                try { encoder.abort(); } catch (_) {}
            }
            try { if (sink) await sink.cleanup(); } catch (_) {}
            var isAbort = !!(e && (e.name === 'AbortError' || (signal && signal.aborted)));
            var isStalled = !!(e && e.code === 'PIPELINE_STALLED');
            // Single canonical emission per failure — per [reference_telemetry_single_emitter].
            // The stall machinery no longer emits its own event (the prior
            // `_ae('pipeline_stalled',…)` calls were removed): the catch
            // emits ONE typed `tool_error{error_type:'pipeline_stalled'}` here
            // for stalls, plus the canonical `audio_stream_pipeline_failed`
            // with `stalled_stage` for dashboard compatibility. For non-stall
            // failures only `audio_stream_pipeline_failed` fires.
            if (isStalled) {
                try {
                    if (typeof window.__ae === 'function') {
                        window.__ae('tool_error', {
                            slug: slug, error_type: 'pipeline_stalled',
                            ext: inputExt, fmt: outputFormat,
                            stage: e.stage || 'unknown',
                            stalled_ms: e.stalledMs || 0,
                            sinkMode: (sink && sink.mode) || 'unknown',
                            hidden: (typeof document !== 'undefined' && document.hidden) ? 1 : 0,
                        });
                    }
                } catch (_) {}
            }
            _ae(isAbort ? 'audio_stream_pipeline_cancelled' : 'audio_stream_pipeline_failed', {
                slug: slug, ext: inputExt, fmt: outputFormat,
                err: ((e && e.message) || '').slice(0, 200),
                stalled_stage: isStalled ? (e.stage || 'unknown') : null,
            });
            throw e;
        }
    }

    /**
     * Parse a single FFmpeg filter expression like
     *   'equalizer=f=2800:t=h:w=1200:g=3.5'
     * into { name: 'equalizer', f: 2800, t: 'h', w: 1200, g: 3.5 }.
     * Numeric values coerced; dB/Hz/ms suffixes stripped.
     */
    function parseFFmpegFilter(expr) {
        var eq = expr.indexOf('=');
        var name = (eq < 0 ? expr : expr.slice(0, eq)).trim();
        var out = { name: name };
        if (eq < 0) return out;
        var pairs = expr.slice(eq + 1).split(':');
        for (var i = 0; i < pairs.length; i++) {
            var p = pairs[i];
            var ki = p.indexOf('=');
            if (ki < 0) continue;
            var k = p.slice(0, ki).trim();
            var v = p.slice(ki + 1).trim();
            var stripped = v.replace(/(dB|Hz|kHz|ms|s)$/i, '');
            var num = parseFloat(stripped);
            out[k] = (!isNaN(num) && stripped !== '') ? num : v;
        }
        return out;
    }

    /** Split + parse a comma-joined FFmpeg filter chain string. */
    function parseFFmpegChain(chainString) {
        return (chainString || '').split(',')
            .map(function (s) { return s.trim(); })
            .filter(Boolean)
            .map(parseFFmpegFilter);
    }

    window.AudioStreamPipeline = {
        version: '1.0.0',
        STREAMING_FILTERS: STREAMING_FILTERS,
        STREAMING_FORMATS: STREAMING_FORMATS,
        canHandle: canHandle,
        process: process,
        parseFFmpegChain: parseFFmpegChain,
        parseFFmpegFilter: parseFFmpegFilter,
        // Test hooks — exposes builders for unit-style assertions.
        _internal: {
            buildChain: buildChain, runChain: runChain,
            makeBiquad: makeBiquad, makeCompressor: makeCompressor,
            makeLimiter: makeLimiter, makeGain: makeGain,
            makeFadeIn: makeFadeIn, makeFadeOut: makeFadeOut,
            makeLinearResampler: makeLinearResampler,
            makePolyphaseResampler: makePolyphaseResampler,
            besselI0: besselI0, kaiserWindow: kaiserWindow,
            designLowpassSinc: designLowpassSinc,
            designPolyphaseFilter: designPolyphaseFilter,
            readWavHeader: readWavHeader, rbjCoeffs: rbjCoeffs,
            // Chunked WAV I/O — consumed by mastering-stream.js (the
            // mastering lane streams WAV itself instead of re-implementing
            // the header walk / 8-24-bit readers / EXTENSIBLE unwrap here).
            decodeWavStream: decodeWavStream, makeWavWriter: makeWavWriter
        }
    };
})();
